import { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { MarkdownViewerProps } from './MarkdownViewer.props';
import { ErrorWrapper, LoaderWrapper, MarkdownContent, MarkdownViewerWrapper } from './MarkdownViewer.styles';
import { Logger } from '../../../utilities';
import HighlightedText from '../../HighlightedText/HighlightedText';

const MarkdownViewer = ({
  src,
  base64Content,
  onCitationPushed = () => {},
  activeCitation = undefined,
}: MarkdownViewerProps) => {
  const [markdownText, setMarkdownText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMarkdown = async () => {
      setLoading(true);
      setError(null);

      try {
        let text = '';

        if (base64Content) {
          // Decode base64 content
          text = atob(base64Content);
        } else if (src) {
          // Fetch from URL
          const response = await fetch(src);
          if (!response.ok) {
            throw new Error('Failed to fetch markdown file');
          }
          text = await response.text();
        } else {
          setError('No markdown source provided');
          setLoading(false);
          return;
        }

        setMarkdownText(text);
      } catch (err) {
        Logger.error('Error loading markdown:', err);
        setError(err instanceof Error ? err.message : 'Failed to load markdown');
      } finally {
        setLoading(false);
      }
    };

    loadMarkdown();
  }, [base64Content, src]);

  const citations = useMemo(() => (activeCitation ? [activeCitation] : []), [activeCitation]);

  if (loading) {
    return (
      <LoaderWrapper>
        <CircularProgress />
      </LoaderWrapper>
    );
  }

  if (error) {
    return <ErrorWrapper>Error: {error}</ErrorWrapper>;
  }

  if (!markdownText) {
    return <ErrorWrapper>No markdown content available</ErrorWrapper>;
  }

  return (
    <MarkdownViewerWrapper>
      <MarkdownContent>
        <HighlightedText sourceText={markdownText} onCitationPushed={onCitationPushed} citations={citations} />
      </MarkdownContent>
    </MarkdownViewerWrapper>
  );
};

export default MarkdownViewer;
